import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "../contexts/LangContext";
import { IMAGES } from "../lib/images";
import { pizzaMenu } from "../lib/pizzaMenu";
import SpiceMeter from "../components/SpiceMeter";
import DietBadge from "../components/DietBadge";
import useSEO from "../lib/useSEO";

export default function Catering() {
  const { t, lang } = useLang();
  const pm = pizzaMenu[lang] || pizzaMenu.de;

  useSEO({
    title: "Leistungen & Genusskarte — Pizza Catering für Hochzeit, Firmenfeier & Events | Tæste",
    description:
      "Unsere Catering-Konzepte für Hoyerswerda und die Lausitz: Rundum-Genuss, Pizza zum Teilen und Streetfood. Handgemachte Pizza aus dem mobilen Pizzaofen für Hochzeit, Geburtstag, Firmenfeier und Events.",
    keywords:
      "Pizza Catering Hoyerswerda, Pizza Catering Lausitz, Catering Hochzeit, Pizza Catering Hochzeit, Catering Firmenfeier, Catering Geburtstag, Catering Events, mobiles Pizza Catering, mobiler Pizzaofen Catering, Catering Lohsa",
    path: "/catering",
  });

  return (
    <div data-testid="catering-page" className="relative overflow-hidden">
      <div className="relative max-w-[1300px] mx-auto px-6 lg:px-12 py-20 md:py-28">
        {/* HERO */}
        <div className="grid md:grid-cols-12 gap-12 items-end mb-16">
          <div className="md:col-span-8">
            <p className="eyebrow">{t.catering.eyebrow}</p>
            <h1 className="font-display text-5xl md:text-7xl mt-3 leading-[1.02]">{t.catering.title}</h1>
          </div>
          <div className="md:col-span-4">
            <p className="text-base text-[hsl(var(--muted-foreground))] leading-relaxed">{t.catering.body}</p>
          </div>
        </div>

        {/* 1. VORSPEISEN */}
        <section data-testid="section-starters" className="mb-24 md:mb-32">
          <div className="flex items-baseline gap-4 mb-3">
            <span className="font-display text-4xl md:text-5xl text-[hsl(var(--brand-gold))]">01</span>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02]">{t.catering.starters_title}</h2>
          </div>
          <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl mb-10">{t.catering.starters_body}</p>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-8">
            {t.catering.starters_items.map(([name, desc], i) => (
              <article key={name} data-testid={`starter-item-${i}`} className="border-t border-border pt-6">
                <h3 className="font-display text-xl md:text-2xl">{name}</h3>
                <p className="mt-2 text-[hsl(var(--muted-foreground))] leading-relaxed">{desc}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 text-xs tracking-widest uppercase font-semibold text-[hsl(var(--primary))]">{t.catering.starters_note}</p>
        </section>

        {/* 2. PIZZEN */}
        <section data-testid="section-pizzas" className="mb-24 md:mb-32">
          <div className="grid md:grid-cols-12 gap-10 items-end mb-12">
            <div className="md:col-span-8">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-4xl md:text-5xl text-[hsl(var(--brand-gold))]">02</span>
                <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02]">{t.catering.pizzas_title}</h2>
              </div>
              <p className="mt-6 text-base md:text-lg text-[hsl(var(--muted-foreground))] max-w-2xl">{pm.intro}</p>
              {/* Legende Veggie / Vegan */}
              <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[hsl(var(--muted-foreground))]" data-testid="diet-legend">
                <span className="inline-flex items-center gap-2"><DietBadge diet="veggie" /><span>Vegetarisch</span></span>
                <span className="inline-flex items-center gap-2"><DietBadge diet="vegan" /><span>Vegan</span></span>
              </div>
            </div>
            <div className="md:col-span-4">
              <div className="aspect-square blob-organic overflow-hidden">
                <img src={IMAGES.pizza1} alt="Handgemachte Pizza aus dem Holzofen" className="w-full h-full object-cover" />
              </div>
            </div>
          </div>

          <div className="space-y-16">
            {pm.groups.map((g, gi) => {
              const groupDiet = g.title.toLowerCase().includes("vegan")
                ? "vegan"
                : g.title.toLowerCase().startsWith("vegetari")
                ? "veggie"
                : null;
              return (
                <div key={g.title} data-testid={`pizza-group-${gi}`}>
                  <p className="eyebrow text-[hsl(var(--brand-gold))] mb-2">{`0${gi + 1}`}</p>
                  <h3 className="font-display text-2xl md:text-3xl tracking-tight inline-flex items-center gap-3">
                    {g.title}
                    {groupDiet && <DietBadge diet={groupDiet} />}
                  </h3>
                  <p className="text-[hsl(var(--muted-foreground))] max-w-3xl mt-3 mb-10">{g.sub}</p>
                <div className="grid md:grid-cols-2 gap-x-14 gap-y-10">
                  {g.items.map((it, ii) => (
                    <article key={it.name} data-testid={`pizza-item-${gi}-${ii}`} className="relative border-t border-border pt-6 pl-4 before:absolute before:left-0 before:top-6 before:bottom-1 before:w-px before:bg-[hsl(var(--brand-gold))]/60">
                      <h4 className="font-display italic text-2xl md:text-3xl text-[hsl(var(--brand-gold))] leading-tight">
                        {it.name}
                        <DietBadge diet={it.diet} />
                        <SpiceMeter level={it.spice || 0} />
                      </h4>
                      {it.intro && (
                        <p className="mt-3 italic text-[hsl(var(--foreground))]/85 leading-relaxed">
                          {it.intro}
                        </p>
                      )}
                      <p className="mt-3 text-[hsl(var(--foreground))]/90 leading-relaxed">{it.desc}</p>
                      <p className="mt-3 text-[11px] tracking-[0.22em] uppercase font-semibold text-[hsl(var(--primary))]">{it.tag}</p>
                    </article>
                  ))}
                </div>
              </div>
              );
            })}
          </div>
        </section>

        {/* 3. DESSERTS */}
        <section data-testid="section-desserts" className="mb-24 md:mb-32">
          <div className="flex items-baseline gap-4 mb-3">
            <span className="font-display text-4xl md:text-5xl text-[hsl(var(--brand-gold))]">03</span>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.02]">{t.catering.desserts_title}</h2>
          </div>
          <p className="text-base md:text-lg text-[hsl(var(--muted-foreground))] leading-relaxed max-w-2xl mb-10">{t.catering.desserts_body}</p>
          <div className="grid md:grid-cols-2 gap-x-14 gap-y-8">
            {t.catering.desserts_items.map(([name, desc], i) => (
              <article key={name} data-testid={`dessert-item-${i}`} className="border-t border-border pt-6">
                <h3 className="font-display text-xl md:text-2xl">{name}</h3>
                <p className="mt-2 text-[hsl(var(--muted-foreground))] leading-relaxed">{desc}</p>
              </article>
            ))}
          </div>
          <p className="mt-8 text-xs tracking-widest uppercase font-semibold text-[hsl(var(--primary))]">{t.catering.desserts_note}</p>
        </section>

        {/* EVENTS */}
        <div className="max-w-3xl">
          <h2 className="font-display text-3xl md:text-5xl mb-6">{t.catering.events_title}</h2>
          <div className="flex flex-wrap gap-2">
            {t.catering.events.map((e, i) => (
              <span key={e} data-testid={`event-chip-${i}`} className="px-4 py-2 text-xs tracking-widest uppercase font-semibold border border-[hsl(var(--border))] bg-[hsl(var(--background))] pill">
                {e}
              </span>
            ))}
          </div>
          <Link to="/kontakt" data-testid="catering-cta" className="mt-10 inline-flex items-center gap-2 px-7 py-4 text-xs tracking-[0.22em] uppercase font-semibold bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))] hover:bg-[hsl(var(--accent))] pill">
            {t.catering.cta} <ArrowRight size={14} />
          </Link>
        </div>
      </div>
    </div>
  );
}
